import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthenticationService, isNullOrUndefined, WebSocketService } from '@sla-ng/core';

import { IAdditionalInfo } from '../../models/additional-info.model';
import { ICleanWindowConfig } from '../../models/clean-window-config.model';
import { IVersion } from '../../models/version.model';
import { CleaningDialogComponent } from '../cleaning-dialog/cleaning-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { VersionDialogComponent } from '../version-dialog/version-dialog.component';
import { MenuItem } from '../../models/menu-item.model';
import { GlobalConfigService } from '../../services/global-config.service';

@Component({
    selector: 'mat-sla-topbar',
    templateUrl: './sla-topbar.component.html',
    styleUrls: [ './sla-topbar.component.scss' ],
})
export class SlaTopbarComponent implements OnInit {
    @Output() public menuToggled: EventEmitter<void> = new EventEmitter<void>();
    @Output() public wpcClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() public logoutClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() public profileClicked: EventEmitter<void> = new EventEmitter<void>();

    @Input() public logoPath: string;
    @Input() public username: string;                          // Optional
    @Input() public workplaceKey: string;                      // Optional
    @Input() public showProfile: boolean;
    @Input() public packageJson: unknown;
    @Input() public version: IVersion;
    @Input() public additionalInfo: IAdditionalInfo[];
    @Input() public showLogout: boolean = true;
    @Input() public showRefresh: boolean = true;                      // Optional
    @Input() public showNavigationAnimation: boolean;
    @Input() public showProgressBar: boolean;
    @Input() public skipLogoutFlow: boolean = false;
    @Input() public hiddenInfos: string[] = [];
    @Input() public cleanConfig: ICleanWindowConfig = {
        showCleanButton: false,
        cleaningTime: 10,
    };

    public fullscreen: boolean;
    public doc: Document = document;

    constructor(
        private myRouter: Router,
        private myAuthenticationService: AuthenticationService,
        private myWebSocketService: WebSocketService,
        private myGlobalConfigService: GlobalConfigService,
        private myDialog: MatDialog) { /** */
    }

    public ngOnInit(): void {
        if (this.showNavigationAnimation) {
            this.listenToRouterEvents();
        }
    }

    public logout(): void {
        this.logoutClicked.emit();
        if (this.skipLogoutFlow) {
            return;
        }

        this.myAuthenticationService.logout().subscribe(() => {
            try {
                const sharedMenuItems: MenuItem[] = this.myGlobalConfigService.getSharedMenuItems();
                if (!isNullOrUndefined(sharedMenuItems) && sharedMenuItems.length > 0) {
                    const logoutProducts: string[] = sharedMenuItems.reduce((prev: string[], currentValue: MenuItem) => {
                        const splitArray: string[] = currentValue.routerLink ? currentValue.routerLink.split('/') : [];
                        const productIndex: number = splitArray.indexOf('#') - 1;
                        return !prev.includes(splitArray[ productIndex ]) ? [ ...prev, (`/${  splitArray.splice(productIndex - 1, 2).join('/')}`) ] : prev;
                    }, []);
                    this.myAuthenticationService.resetSharedMenuTokens(logoutProducts);
                }
            } catch (e) {
                console.log(e);
            }
            this.myWebSocketService.closeWebsocket();
            void this.myRouter.navigateByUrl('login');
        });
    }

    public refresh(): void {
        window.location.reload();
    }

    public openVersionDialog(): void {
        this.myDialog.open(VersionDialogComponent, {
            data: {
                packageJson: this.packageJson,
                additionalInfo: this.additionalInfo,
                version: this.version,
                hiddenInfos: this.hiddenInfos,
            },
        });
    }

    public openProfileDialog(): void {
        this.profileClicked.emit();

    }

    public onTopBarLogoClick(): void {
        void this.myRouter.navigateByUrl('landing');
    }

    public clickedWPC(): void {
        this.wpcClicked.emit();
    }

    public requestFullscreen(): void {
        const doc: Document = document;
        if (!doc.fullscreenElement) {
            doc.documentElement.requestFullscreen().then(() => {
                this.fullscreen = true;
            }).catch(() => this.fullscreen = false);
        } else {
            if (doc.exitFullscreen) {
                void doc.exitFullscreen().then(() => {
                    this.fullscreen = false;
                });
            }
        }
    }

    public lockForCleaning(): void {
        const ref: MatDialogRef<ConfirmDialogComponent> = this.myDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                caption: 'sla.ui-material.message.confirmation.label', message: 'sla.ui-material.clean.confirm.message.label', showReject: true, touch: true,
            },
        });
        ref.afterClosed().subscribe((accepted: boolean) => {
            if (accepted) {
                this.myDialog.open(CleaningDialogComponent, {
                    data: {
                        time: this.cleanConfig?.cleaningTime,
                    },
                    panelClass: 'ui-mat-clean',
                });
            }
        });
    }

    private listenToRouterEvents(): void {
        this.myRouter.events.subscribe((event: NavigationStart | NavigationEnd | NavigationCancel) => {
            if (event instanceof NavigationStart) {
                this.showProgressBar = true;
            } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
                this.showProgressBar = false;
            }
        });
    }
}
